
import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

const PlanLineGraph = ({ data, storage }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    let delayed = false;

    if (!chartRef.current || !data) return;

    const labels = data?.map((item) => item.label || item.key);
    let values = data?.map((item) => item.value);

    let unit = "B"; 
    let step = 1;

    if (storage) {
      // Determine the maximum and minimum values
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);

      // Round the maximum and minimum values
      const roundedMaxValue = Math.ceil(maxValue);
      const roundedMinValue = Math.floor(minValue);

      // Calculate the range of values
      const range = roundedMaxValue - roundedMinValue;

      // Calculate a common unit for all values
      unit = "B"; // Update unit based on condition
      let divisor = 1;
      if (roundedMaxValue >= 1024 ** 4) {
        unit = "TB";
        divisor = 1024 ** 4;
      } else if (roundedMaxValue >= 1024 ** 3) {
        unit = "GB";
        divisor = 1024 ** 3;
      } else if (roundedMaxValue >= 1024 ** 2) {
        unit = "MB";
        divisor = 1024 ** 2;
      } else if (roundedMaxValue >= 1024) {
        unit = "KB";
        divisor = 1024;
      }

      step = 1;
      if (range > 5) {
        step = Math.ceil(range / 5); 
      }

      // Normalize values if storage is true
      values = values.map((value) => value / divisor);
    }

    const ctx = chartRef.current.getContext("2d");

    // Destroy existing chart if it exists
    let chartInstance = null;
    if (chartRef.current.chart) {
      chartRef.current.chart.destroy();
    }
    // Create new chart instance
    chartInstance = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Value",
            data: values,
            borderColor: "rgba(52, 98, 157, 1)",
            pointBackgroundColor: "rgba(52, 98, 157, 1)",
            backgroundColor: "rgba(52, 98, 157, 0.25)",
            borderWidth: 2,
            pointRadius: 2,
            fill:true,
          },
        ],
      },
      options: {
        responsive: true,
        animation: {
          onComplete: () => {
            delayed = true;
          },
          delay: (context) => {
            let delay = 0;
            if (
              context.type === "data" &&
              context.mode === "default" &&
              !delayed
            ) {
              delay = context.dataIndex * 300 + context.datasetIndex * 100;
            }
            return delay;
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            grid: {
              display: false,
            },
            ticks: {
              callback: (value) =>
                storage ? value.toFixed(2) + " " + unit : value,
              stepSize: storage ? step : undefined,
            },
          },
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
      },
    });

    // Update the chart reference
    chartRef.current.chart = chartInstance;

    // Cleanup function to destroy the chart when component unmounts
    return () => {
      if (chartRef.current && chartRef.current.chart) {
        chartRef.current.chart.destroy();
      }
    };
  }, [data, storage]);

  return <canvas ref={chartRef} />;
};

export default PlanLineGraph;

